import { faucetState, type FaucetState, type Result } from '@/net/api';

/**
 * The faucet's state, kept once for every surface that shows it (the Free tokens screen, home's entry and your own
 * profile's): one GET /api/faucet at a time, fresh for a short while, replaced outright by a claim's answer, and
 * dropped with the account. Nothing here draws; the surfaces subscribe and repaint.
 *
 * The state is the server's and is only ever read for the SESSION player: there is no name in the request, so
 * another player's page cannot be given one, and the entry is only ever offered on your own.
 */

/** A read is reused for this long unless a caller forces one (a screen opening always does). */
export const FAUCET_FRESH_MS = 30_000;

type Fetcher = () => Promise<Result<FaucetState>>;

let state: FaucetState | null = null;
let owner: string | null = null;
let at = 0;
let inflight: Promise<FaucetState | null> | null = null;
/** The server said the faucet is off (`faucet-off`: the flag, or an origin that is not public https): nothing is offered. */
let off = false;
let fetcher: Fetcher = faucetState;
let clock: () => number = Date.now;
const subscribers = new Set<() => void>();

function emit(): void {
  for (const fn of [...subscribers]) fn();
}

/** True once the server has answered `faucet-off`: the entries hide and the screen says it is not available. */
export function faucetIsOff(): boolean {
  return off;
}

/** What is known right now, for `username` (null for anyone else's account: nothing carries over). */
export function currentFaucet(username: string | null): FaucetState | null {
  return username !== null && username === owner ? state : null;
}

export function subscribeFaucet(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** The state for `username`, from the server. A read in flight is shared; a fresh one is reused unless `force`. */
export function loadFaucet(username: string, opts: { force?: boolean } = {}): Promise<FaucetState | null> {
  if (owner !== username) {
    state = null;
    owner = username;
    at = 0;
  }
  if (!opts.force && state && clock() - at < FAUCET_FRESH_MS) return Promise.resolve(state);
  if (inflight) return inflight;
  const asked = username;
  inflight = fetcher()
    .then((result) => {
      // The account moved on while it was in flight: that answer is not theirs.
      if (owner !== asked) return null;
      if (result.ok) {
        state = result.value;
        off = false;
        at = clock();
        emit();
        return state;
      }
      if (result.error === 'faucet-off') {
        state = null;
        off = true;
        emit();
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** A claim's answer: the new state, now, for everyone showing it. */
export function setFaucet(username: string, next: FaucetState): void {
  owner = username;
  state = next;
  at = clock();
  emit();
}

/** Sign-out, or another account: forget everything. */
export function clearFaucet(): void {
  off = false;
  state = null;
  owner = null;
  at = 0;
  emit();
}

/** Tests: swap the network and the clock, and start empty. */
export function resetFaucetForTests(opts: { fetch?: Fetcher; clock?: () => number } = {}): void {
  state = null;
  owner = null;
  at = 0;
  off = false;
  inflight = null;
  fetcher = opts.fetch ?? faucetState;
  clock = opts.clock ?? Date.now;
  subscribers.clear();
}
